import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import HeroSection from '../components/ui/HeroSection';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import Divider from '../components/ui/Divider';
import { 
  AwardIcon, 
  HotelIcon, 
  CustomerServiceIcon, 
  TeamIcon, 
  ContactsIcon,
  HomeIcon,
  AuctionIcon,
  MapPinIcon,
  EventIcon,
  ExperienceIcon
} from '../utils/IconWrapper';
import { useLanguage } from '../i18n/LanguageContext';
import HeaderBanner from '../components/ui/HeaderBanner';

// Immagini temporanee da sostituire con immagini reali quando disponibili
const heroImage = 'https://images.unsplash.com/photo-1560448204-603b3fc33ddc?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';
const aboutImage = 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';

// Immagini per i servizi
const shortTermRentalImage = 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';
const longTermRentalImage = 'https://images.unsplash.com/photo-1484154218962-a197022b5858?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';
const auctionsImage = 'https://geauction.com/wp-content/uploads/2018/06/Your-First-Auction-What-You-Need-to-Know.jpg';
const buyAndSellImage = 'https://images.unsplash.com/photo-1560448204-603b3fc33ddc?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';
const eventsImage = 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80';
const experiencesImage = 'https://www.dailymood.it/wp-content/uploads/2018/04/home-35.jpg';

const AboutRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutImage = styled.div`
  position: relative;
  height: 500px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    transition: transform 0.5s ease, box-shadow 0.5s ease;
    
    &:hover {
      transform: scale(1.02);
      box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
    }
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -20px;
    right: -20px;
    width: 80%;
    height: 80%;
    border: 2px solid var(--accent-primary);
    z-index: -1;
    border-radius: 8px;
  }
  
  @media (max-width: 992px) {
    height: 400px;
  }
`;

const AboutContent = styled.div`
  padding: 2rem 0;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 4rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled(motion.div)`
  text-align: center;
  padding: 0;
  background-color: var(--bg-secondary);
  border: 1px solid rgba(0, 71, 68, 0.15);
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.25);
    border-color: #004744;
  }
`;

const ValueImage = styled.div<{ backgroundImage: string }>`
  width: 100%;
  height: 180px;
  background-image: url(${props => props.backgroundImage});
  background-size: cover;
  background-position: center;
`;

const ValueContent = styled.div`
  padding: 1.5rem;
`;

const ValueIcon = styled.div`
  font-size: 2.8rem;
  color: #004744;
  margin-bottom: 1.5rem;
  opacity: 0.9;
  transition: opacity 0.3s ease, transform 0.3s ease;
  
  ${ValueCard}:hover & {
    opacity: 1;
    transform: scale(1.1);
  }
`;

const ValueTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  position: relative;
  color: #004744;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 2px;
    background-color: #004744;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  ${ValueCard}:hover &:after {
    opacity: 1;
  }
`;

const ValueDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.7;
  margin-top: 1rem;
`;

const ValueCardNeutral = styled(motion.div)`
  text-align: center;
  padding: 0;
  background-color: var(--bg-secondary);
  border: 1px solid rgba(0,0,0,0.07);
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.25);
    border-color: rgba(0,0,0,0.15);
  }
`;

const ValueIconNeutral = styled.div`
  font-size: 2.8rem;
  color: var(--accent-primary);
  margin-bottom: 1.5rem;
  opacity: 0.9;
  transition: opacity 0.3s ease, transform 0.3s ease;
  ${ValueCardNeutral}:hover & {
    opacity: 1;
    transform: scale(1.1);
  }
`;

const ValueTitleNeutral = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  position: relative;
  color: var(--text-primary);
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 2px;
    background-color: var(--accent-primary);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  ${ValueCardNeutral}:hover &:after {
    opacity: 1;
  }
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2.5rem;
  padding: 2rem;
  background: linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.06));
  border-radius: 12px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const StatItem = styled(motion.div)`
  text-align: center;
  padding: 1.5rem;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.03);
  transition: transform 0.3s ease, background-color 0.3s ease;
  border: 1px solid rgba(0, 71, 68, 0.15);
  
  &:hover {
    transform: translateY(-5px);
    background-color: rgba(0, 71, 68, 0.07);
  }
`;

const StatNumber = styled.div`
  font-weight: 700;
  margin-bottom: 0.8rem;
  line-height: 1;
  font-size: 2.5rem;
  background: linear-gradient(135deg, #004744, #004744);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #004744;
  font-weight: 500;
`;

const About: React.FC = () => {
  const { t, language } = useLanguage();
  
  return (
    <>
      <HeaderBanner />
      
      {/* 1. Hero Section */}
      <HeroSection
        title={language === 'it' ? 'Chi Siamo' : 'About Us'}
        subtitle={language === 'it' 
          ? 'Scopri la nostra storia e i nostri valori' 
          : 'Discover our story and our values'}
        backgroundImage={heroImage}
        height="70vh"
      />
      
      <Divider variant="diamond" />
      
      {/* 2. About Section */}
      <Section
        backgroundColor="var(--bg-primary)"
        padding="8rem 0"
      >
        <AboutRow>
          <AboutImage>
            <img src={aboutImage} alt="J-O-H Team" />
          </AboutImage>
          
          <AboutContent>
            <h2 className="text-3xl font-semibold mb-6">
              {language === 'it' ? 'La Nostra Storia' : 'Our Story'}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('about.story.paragraph1')}
            </p>
            <p className="text-gray-600 mb-6">
              {t('about.story.paragraph2')}
            </p>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci' : 'Contact Us'}
            </Button>
          </AboutContent>
        </AboutRow>
      </Section>
      
      <Divider variant="diamond" />
      
      {/* 3. Values Section */}
      <Section
        backgroundColor="var(--bg-secondary)"
        padding="8rem 0"
      >
        <h2 className="text-center text-3xl font-semibold mb-12">
          {language === 'it' ? 'I Nostri Servizi' : 'Our Services'}
        </h2>
        
        <ValuesGrid>
          <ValueCardNeutral>
            <ValueImage backgroundImage={shortTermRentalImage} />
            <ValueContent>
              <ValueIconNeutral>
                <HotelIcon />
              </ValueIconNeutral>
              <ValueTitleNeutral>{language === 'it' ? 'Affitti Brevi' : 'Short-Term Rentals'}</ValueTitleNeutral>
              <ValueDescription>
                {language === 'it' ? 'Offriamo alloggi a breve termine di lusso con servizi premium. Ogni proprietà è selezionata per garantire comfort, stile e un\'esperienza impeccabile.' : 'We offer luxury short-term accommodations with premium services. Each property is selected to ensure comfort, style, and an impeccable experience.'}
              </ValueDescription>
            </ValueContent>
          </ValueCardNeutral>
          <ValueCardNeutral>
            <ValueImage backgroundImage={longTermRentalImage} />
            <ValueContent>
              <ValueIconNeutral>
                <HomeIcon />
              </ValueIconNeutral>
              <ValueTitleNeutral>{language === 'it' ? 'Affitti Medio/Lungo Termine' : 'Medium/Long-Term Rentals'}</ValueTitleNeutral>
              <ValueDescription>
                {language === 'it' ? 'Soluzioni abitative completamente arredate per soggiorni prolungati, ideali per trasferimenti di lavoro o per chi desidera vivere un\'esperienza in una nuova località.' : 'Fully furnished housing solutions for extended stays, ideal for work relocations or for those who want to experience living in a new location.'}
              </ValueDescription>
            </ValueContent>
          </ValueCardNeutral>
          <ValueCardNeutral>
            <ValueImage backgroundImage={auctionsImage} />
            <ValueContent>
              <ValueIconNeutral>
                <AuctionIcon />
              </ValueIconNeutral>
              <ValueTitleNeutral>{language === 'it' ? 'Aste Immobiliari' : 'Real Estate Auctions'}</ValueTitleNeutral>
              <ValueDescription>
                {language === 'it' ? 'Il nostro team di esperti offre consulenza pre-asta, assistenza durante la procedura e supporto post-aggiudicazione per accedere a opportunità immobiliari esclusive.' : 'Our team of experts offers pre-auction consulting, assistance during the procedure, and post-award support to access exclusive real estate opportunities.'}
              </ValueDescription>
            </ValueContent>
          </ValueCardNeutral>
          <ValueCardNeutral>
            <ValueImage backgroundImage={buyAndSellImage} />
            <ValueContent>
              <ValueIconNeutral>
                <MapPinIcon />
              </ValueIconNeutral>
              <ValueTitleNeutral>{language === 'it' ? 'Compravendite' : 'Buy and Sell'}</ValueTitleNeutral>
              <ValueDescription>
                {language === 'it' ? 'Servizi completi di consulenza immobiliare per acquirenti e venditori, dalla valutazione iniziale alla negoziazione e finalizzazione della transazione.' : 'Complete real estate consulting services for buyers and sellers, from initial valuation to negotiation and transaction finalization.'}
              </ValueDescription>
            </ValueContent>
          </ValueCardNeutral>
          <ValueCardNeutral>
            <ValueImage backgroundImage={eventsImage} />
            <ValueContent>
              <ValueIconNeutral>
                <EventIcon />
              </ValueIconNeutral>
              <ValueTitleNeutral>{language === 'it' ? 'Eventi' : 'Events'}</ValueTitleNeutral>
              <ValueDescription>
                {language === 'it' ? 'Trasformiamo le nostre location esclusive in palcoscenici per eventi privati e aziendali, occupandoci di ogni dettaglio dalla pianificazione all\'esecuzione.' : 'We transform our exclusive locations into stages for private and corporate events, taking care of every detail from planning to execution.'}
              </ValueDescription>
            </ValueContent>
          </ValueCardNeutral>
          <ValueCardNeutral>
            <ValueImage backgroundImage={experiencesImage} />
            <ValueContent>
              <ValueIconNeutral>
                <ExperienceIcon />
              </ValueIconNeutral>
              <ValueTitleNeutral>{language === 'it' ? 'Servizi/Experience' : 'Services/Experience'}</ValueTitleNeutral>
              <ValueDescription>
                {language === 'it' ? 'Esperienze personalizzate che arricchiscono il soggiorno, dai tour enogastronomici alle lezioni di cucina, dalle escursioni private alle lezioni di italiano.' : 'Personalized experiences that enrich your stay, from food and wine tours to cooking classes, from private excursions to Italian lessons.'}
              </ValueDescription>
            </ValueContent>
          </ValueCardNeutral>
        </ValuesGrid>
      </Section>
      
      <Divider variant="diamond" />
      
      {/* 4. Stats Section */}
      <Section
        backgroundColor="var(--bg-primary)"
        padding="8rem 0"
      >
        <h2 className="text-center text-3xl font-semibold mb-12">
          {language === 'it' ? 'Crescita Anno Dopo Anno' : 'Our Growth Year After Year'}
        </h2>
        
        <StatsSection>
          <StatItem
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ y: -10, transition: { duration: 0.2 } }}
          >
            <StatNumber>200+</StatNumber>
            <StatLabel>{t('about.properties_managed')}</StatLabel>
          </StatItem>
          
          <StatItem
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ y: -10, transition: { duration: 0.2 } }}
          >
            <StatNumber>5000+</StatNumber>
            <StatLabel>{t('about.happy_clients')}</StatLabel>
          </StatItem>
          
          <StatItem
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            whileHover={{ y: -10, transition: { duration: 0.2 } }}
          >
            <StatNumber>15</StatNumber>
            <StatLabel>{t('about.cities_covered')}</StatLabel>
          </StatItem>
          
          <StatItem
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            whileHover={{ y: -10, transition: { duration: 0.2 } }}
          >
            <StatNumber>13</StatNumber>
            <StatLabel>{t('about.years_experience')}</StatLabel>
          </StatItem>
        </StatsSection>
      </Section>
      
      <Divider variant="diamond" />
      
      {/* 5. Services Section */}
      <Section
        backgroundColor="var(--bg-secondary)"
        padding="8rem 0 4rem"
      >
        <h2 className="text-center text-3xl font-semibold mb-12">
          {language === 'it' ? 'I Nostri Valori' : 'Our Values'}
        </h2>
        
        <ValuesGrid>
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <ValueIcon>
              <AwardIcon />
            </ValueIcon>
            <ValueTitle>{t('about.values.excellence.title')}</ValueTitle>
            <ValueDescription>
              {t('about.values.excellence.description')}
            </ValueDescription>
          </ValueCard>
          
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <ValueIcon>
              <HotelIcon />
            </ValueIcon>
            <ValueTitle>{t('about.values.luxury.title')}</ValueTitle>
            <ValueDescription>
              {t('about.values.luxury.description')}
            </ValueDescription>
          </ValueCard>
          
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <ValueIcon>
              <CustomerServiceIcon />
            </ValueIcon>
            <ValueTitle>{t('about.values.service.title')}</ValueTitle>
            <ValueDescription>
              {t('about.values.service.description')}
            </ValueDescription>
          </ValueCard>
          
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <ValueIcon>
              <TeamIcon />
            </ValueIcon>
            <ValueTitle>{t('about.values.integrity.title')}</ValueTitle>
            <ValueDescription>
              {t('about.values.integrity.description')}
            </ValueDescription>
          </ValueCard>
          
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <ValueIcon>
              <ContactsIcon />
            </ValueIcon>
            <ValueTitle>{t('about.values.community.title')}</ValueTitle>
            <ValueDescription>
              {t('about.values.community.description')}
            </ValueDescription>
          </ValueCard>
          
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.0 }}
          >
            <ValueIcon>
              <AwardIcon />
            </ValueIcon>
            <ValueTitle>{t('about.values.innovation.title')}</ValueTitle>
            <ValueDescription>
              {t('about.values.innovation.description')}
            </ValueDescription>
          </ValueCard>
        </ValuesGrid>
      </Section>
      
      <Divider variant="diamond" />
      
      {/* CTA Section */}
      <Section
        backgroundColor="var(--bg-primary)"
        padding="6rem 0"
      >
        <div style={{ textAlign: 'center' }}>
          <h2 className="text-3xl font-semibold mb-6">
            {language === 'it' ? 'Pronto per iniziare?' : 'Ready to get started?'}
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            {language === 'it' 
              ? 'Contattaci oggi stesso per scoprire come possiamo aiutarti a trovare la tua casa ideale.'
              : 'Contact us today to discover how we can help you find your ideal home.'}
          </p>
          <Button size="large" variant="green" to="/contact">
            {language === 'it' ? 'Contattaci Ora' : 'Contact Us Now'}
          </Button>
        </div>
      </Section>
    </>
  );
};

export default About; 